/* FunnelChart — horizontal stacked-bar funnel for the primary role.
   Each stage is a labeled segment; widths proportional to counts. */

import type { JobPosting } from '@/modules/job/services/job.api';

export interface FunnelChartProps {
    job: JobPosting | null;
    surface?: string;
    text?: string;
    muted?: string;
    rule?: string;
}

function hash(s: string) {
    return s.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xffff, 7);
}

const STAGES = [
    { key: 'sourced', label: 'Sourced', color: 'rgb(0 132 255)' },
    { key: 'screened', label: 'Screened', color: 'rgb(86 218 139)' },
    { key: 'interview', label: 'Interview', color: 'rgb(239 154 91)' },
    { key: 'offer', label: 'Offer', color: 'rgb(239 64 168)' },
];

export function FunnelChart({
    job,
    surface = 'rgba(255,255,255,0.7)',
    text = '#2a241e',
    muted = '#9c948a',
    rule = 'rgba(120, 90, 50, 0.08)',
}: FunnelChartProps) {
    if (!job) {
        return null;
    }
    const seed = hash(job._id);
    const counts: Record<string, number> = {
        sourced: 35 + (seed % 23),
        screened: 9 + (seed % 8),
        interview: 3 + (seed % 4),
        offer: seed % 3,
    };
    const max = counts.sourced;

    return (
        <div
            style={{
                background: surface,
                borderRadius: 18,
                padding: 18,
                border: `1px solid ${rule}`,
                fontFamily: 'var(--v2-font-sans)',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: text, letterSpacing: '-0.01em' }}>Funnel · {job.title}</span>
                <span style={{ fontSize: 10, color: muted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>this week</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {STAGES.map((stage) => {
                    const v = counts[stage.key];
                    const pct = (v / max) * 100;
                    return (
                        <div key={stage.key} style={{ display: 'grid', gridTemplateColumns: '78px 1fr 32px', gap: 10, alignItems: 'center' }}>
                            <span style={{ fontSize: 11.5, color: muted, fontWeight: 500 }}>{stage.label}</span>
                            <div style={{ height: 18, background: rule, borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: `${pct}%`,
                                        background: stage.color,
                                        borderRadius: 4,
                                        transition: 'width 320ms cubic-bezier(0.16, 1, 0.3, 1)',
                                    }}
                                />
                            </div>
                            <span
                                style={{
                                    fontFamily: 'var(--v2-font-serif)',
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: text,
                                    textAlign: 'right',
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {v}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
